import { useEffect } from 'react';

/**
 * Custom hook to lock the theme and prevent browser/system theme changes from affecting the page
 * @param {string} lockedTheme - The theme to lock ('light' or 'dark')
 */
const useLockedTheme = (lockedTheme) => {
  useEffect(() => {
    // Remove system preference listeners if any exist
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const preventSystemThemeChange = (e) => {
      e.preventDefault(); // Just in case
      applyTheme(lockedTheme);
    };

    // Apply the locked theme
    const applyTheme = (theme) => {
      document.documentElement.setAttribute('data-theme', theme);
      
      // Also set class-based theme for compatibility
      if (theme === 'dark') {
        document.documentElement.classList.add('dark');
        document.documentElement.classList.remove('light');
      } else {
        document.documentElement.classList.add('light');
        document.documentElement.classList.remove('dark');
      }
      
      // Set CSS custom properties for additional theme control
      document.documentElement.style.setProperty('--forced-theme', theme);
      
      // Force color scheme for better browser compatibility
      document.documentElement.style.colorScheme = theme;
    };

    // Apply the locked theme immediately
    applyTheme(lockedTheme);

    // Prevent system theme overrides
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', preventSystemThemeChange);
    } else if (mediaQuery.addListener) {
      // Fallback for older browsers
      mediaQuery.addListener(preventSystemThemeChange);
    }

    // Additional protection: Override any auto theme switching
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && 
            (mutation.attributeName === 'data-theme' || 
             mutation.attributeName === 'class')) {
          // Re-apply our locked theme if something tries to change it
          const currentTheme = document.documentElement.getAttribute('data-theme');
          if (currentTheme !== lockedTheme) {
            applyTheme(lockedTheme);
          }
        }
      });
    });

    // Observe changes to the document element
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme', 'class']
    });

    return () => {
      // Clean up listener on unmount
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', preventSystemThemeChange);
      } else if (mediaQuery.removeListener) {
        mediaQuery.removeListener(preventSystemThemeChange);
      }
      
      // Disconnect the mutation observer
      observer.disconnect();
      
      // Optionally restore original theme on unmount
      // document.documentElement.removeAttribute('data-theme');
      // document.documentElement.style.removeProperty('--forced-theme');
      // document.documentElement.style.removeProperty('color-scheme');
    };
  }, [lockedTheme]);
};

export default useLockedTheme;